// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button } from 'src/design-system'
import { Sparkle } from '@phosphor-icons/react'
import * as React from 'react'
import { AiSkillContentForm } from 'components/ai/forms/ai-skill-content-form'
import { AiVmMap, AiVmMapKey } from 'components/ai/content/map'
import { store } from 'store/index'
import { errorToast } from 'app/packs/src/utils/error-toast'
import { successToast } from 'app/packs/src/utils/success-toast'
import { onUseSkillContentGeneration } from './on-use-skill-generation-content'
import { Skill } from 'store/modules/skills'
import { SkillVariant } from 'store/modules/skill-variants'
import { EditorContext } from 'components/atoms/editor'

type AiRegenerateButtonProps = {
  skillName: string
  teamType?: string
  showWarning: boolean
  onCancel: () => void
  skill: Skill
  skillVariant: SkillVariant
  skillDescriptionEditor: React.RefObject<EditorContext>
  skillHasContent: boolean
  contentType: AiVmMapKey
  source: string
}

export const AiRegenerateButton: React.FC<AiRegenerateButtonProps> = ({
  skillName,
  teamType,
  showWarning,
  onCancel,
  skill,
  skillVariant,
  skillDescriptionEditor,
  skillHasContent,
  contentType,
  source,
}) => {
  const [isGenerating, setIsGenerating] = React.useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentTaskId, setCurrentTaskId] = React.useState<string | null>(null)
  const [isCancelled, setIsCancelled] = React.useState(false)
  const isMountedRef = React.useRef(true)

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const aiVm = React.useMemo(() => {
    return new AiVmMap[contentType](source)
  }, [contentType, source])

  const startGeneration = async (
    additionalInputs: Record<string, unknown> | null
  ) => {
    if (!isMountedRef.current) return

    setIsGenerating(true)
    setIsCancelled(false)

    try {
      const taskId = await aiVm.onSubmit({
        skillName: skill.name,
        ...additionalInputs,
      })

      if (!taskId) {
        if (isMountedRef.current) {
          errorToast('Failed to start generation. Please try again.')
        }
        return
      }

      if (!isMountedRef.current) return
      setCurrentTaskId(taskId)

      await store.asyncTasks.byId(taskId)?.pollOne()
      const task = store.asyncTasks.byId(taskId)
      if (isCancelled || !isMountedRef.current) {
        return
      }

      if (task?.isSuccess && task.output) {
        await onUseSkillContentGeneration(
          task.output,
          skill,
          skillVariant,
          skillDescriptionEditor,
          Boolean(skillHasContent),
          false
        )
        if (isMountedRef.current) {
          successToast('Content generated successfully!')
          onCancel()
        }
        return
      }

      if (!isMountedRef.current) return
      await store.asyncTasks.byId(taskId)?.pollOne()
      const asyncTask = store.asyncTasks.byId(taskId)

      if (asyncTask?.isSuccess && asyncTask.output) {
        await onUseSkillContentGeneration(
          asyncTask.output,
          skill,
          skillVariant,
          skillDescriptionEditor,
          Boolean(skillHasContent),
          false
        )
        if (isMountedRef.current) {
          successToast('Content generated successfully!')
          onCancel()
        }
      } else if (asyncTask?.isFailure) {
        if (isMountedRef.current) {
          errorToast('Generation failed. Please try again.')
        }
      } else {
        if (isMountedRef.current) {
          errorToast('Generation did not complete. Please try again.')
        }
      }
    } catch (error) {
      if (!isCancelled && isMountedRef.current) {
        errorToast('An error occurred during generation. Please try again.')
      }
    } finally {
      if (isMountedRef.current) {
        setIsGenerating(false)
        setCurrentTaskId(null)
        setIsCancelled(false)
      }
    }
  }

  const cancelGeneration = () => {
    if (!isMountedRef.current) return
    setIsCancelled(true)
    setIsGenerating(false)
    setCurrentTaskId(null)
  }

  return (
    <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200 relative">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-semibold text-xl text-gray-900">AI Regenerate</h3>
        <Sparkle className="h-4 w-4 text-pink-600 mb-1" weight="fill" />
      </div>

      <div className="space-y-6">
        <AiSkillContentForm
          inputs={{
            skillName,
            teamType,
            showWarning,
          }}
          startGeneration={startGeneration}
          onCancel={() => {
            if (isGenerating) {
              cancelGeneration()
            } else {
              onCancel()
            }
          }}
          isGenerating={isGenerating}
        />
      </div>
    </div>
  )
}
